import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';

/**
 * Comprobantes que los participantes mandan por WhatsApp al número de
 * TandaPlus (contrato B de `comprobantes-whatsapp-diseno.md`).
 *
 * La IA sólo sugiere: nada queda registrado hasta que el organizador confirma
 * aquí. El back puede no tener todavía estas rutas (producción, al 30-sep): con
 * 404 el contador queda en `null` y la bandeja no se ofrece en ningún lado.
 */

export type EstadoInbound =
    | 'recibido'
    | 'pregunta_pendiente'
    | 'por_asignar'
    | 'por_revisar'
    | 'descartado_auto'
    | 'sin_identificar'
    | 'asignado'
    | 'registrado'
    | 'descartado';

export type FiltroInbound = 'por_revisar' | 'por_asignar' | 'all';

export type MotivoDescarte = 'no_es_pago' | 'no_es_de_mi_tanda' | 'duplicado' | 'otro';

export interface InboundSugerencia {
    idu_split: string;
    ronda: number;
    idu_tanda_number: string;
    numero: number;
    amt_pendiente: number;
}

export interface InboundLecturaIa {
    es_comprobante: boolean;
    monto: number | null;
    moneda: string | null;
    fecha_hora: string | null;
    clave_rastreo: string | null;
    banco_origen: string | null;
    ordenante: string | null;
    beneficiario: string | null;
    confianza: number;
    /** No está en el contrato B; si el back lo agrega, se pinta ("SPEI"). */
    tipo?: string | null;
}

export interface InboundReceipt {
    idu_inbound: string;
    date_received: string;
    num_whatsapp_masked: string | null;
    cod_status: EstadoInbound;
    cod_match: string | null;
    tanda: { idu_tanda: string; name: string } | null;
    participante: { idu_participant: string; nombre: string } | null;
    sugerencia: InboundSugerencia | null;
    ia: InboundLecturaIa | null;
    flags: string[];
    /** Ruta relativa del proxy (`/api/v1/inbound-receipts/:id/archivo`). */
    thumb_url: string | null;
    /**
     * La lectura con IA es sólo para organizadores Pro. `false` con
     * `ia_motivo` dice por qué no hubo lectura; el monto se precarga entonces
     * con el de la ronda. Ausente = back anterior a este campo.
     */
    ia_disponible?: boolean;
    ia_motivo?: 'no_pro' | 'tope_mensual' | 'apagada' | 'error' | null;
    /** Opcional: si el back lo manda, sirve para decidir imagen o PDF. */
    name_mimetype?: string | null;
}

export interface InboundPagina {
    data: InboundReceipt[];
    total: number;
}

export interface InboundContador {
    por_revisar: number;
    por_asignar: number;
}

/**
 * Una ronda/número pendiente del participante (`GET /:id/opciones`).
 * El contrato B no fija los nombres: se normalizan en `aOpcion`.
 */
export interface InboundOpcion {
    idu_tanda: string | null;
    name_tanda: string | null;
    idu_split: string;
    ronda: number | null;
    idu_tanda_number: string;
    numero: number | null;
    amt_pendiente: number | null;
    date_due: string | null;
    idu_share: string | null;
    nombre: string | null;
    cod_currency: string | null;
}

export interface ConfirmarInbound {
    idu_split: string;
    idu_tanda_number: string;
    idu_share?: string;
    registrar_abono: boolean;
    amt_abono?: number;
    des_abono?: string;
    /** Igual que en `POST /abonos`: false revisa el duplicado (409), true lo registra. */
    confirmar_duplicado?: boolean;
}

export interface ResultadoConfirmar {
    idu_receipt: string;
    idu_abono: string | null;
    aplicado_a: { ronda: number; monto: number }[];
}

@Injectable({ providedIn: 'root' })
export class InboundReceiptsService {
    private readonly _base = `${environment.apiUrl}/inbound-receipts`;

    /**
     * `undefined`: todavía no se pregunta. `null`: el back no tiene la ruta
     * (o no se pudo saber); en ese caso nadie ofrece la bandeja.
     */
    private readonly _contador = new BehaviorSubject<InboundContador | null | undefined>(undefined);

    constructor(private _http: HttpClient) {}

    get contador$(): Observable<InboundContador | null | undefined> {
        return this._contador.asObservable();
    }

    get contador(): InboundContador | null | undefined {
        return this._contador.value;
    }

    /** Pide el contador y lo publica. Un 404 lo deja en `null`; nunca truena. */
    refrescarContador(): Observable<InboundContador | null> {
        return this._http.get<{ data?: Partial<InboundContador> } & Partial<InboundContador>>(`${this._base}/count`).pipe(
            map((res) => {
                const d = res?.data ?? res ?? {};
                return {
                    por_revisar: Number(d.por_revisar ?? 0) || 0,
                    por_asignar: Number(d.por_asignar ?? 0) || 0,
                };
            }),
            tap((c) => this._contador.next(c)),
            catchError((e: HttpErrorResponse) => {
                // 404 = back viejo. Otro error (red, 500) no apaga una bandeja
                // que ya se había visto: se conserva el último valor bueno.
                if (e?.status === 404 || this._contador.value === undefined) this._contador.next(null);
                return of(this._contador.value ?? null);
            }),
        );
    }

    listar(status: FiltroInbound, iduTanda?: string | null, page = 1): Observable<InboundPagina> {
        let params = new HttpParams().set('status', status).set('page', page);
        if (iduTanda) params = params.set('idu_tanda', iduTanda);
        return this._http.get<unknown>(this._base, { params }).pipe(map(aPagina));
    }

    /** URL absoluta del archivo (proxy con sesión; nunca la URL firmada de S3). */
    urlArchivo(r: Pick<InboundReceipt, 'idu_inbound' | 'thumb_url'>): string {
        const rel = r.thumb_url || `/api/v1/inbound-receipts/${r.idu_inbound}/archivo`;
        if (/^https?:\/\//.test(rel)) return rel;
        // `thumb_url` llega como `/api/v1/...`; apiUrl ya termina en `/api/v1`.
        const sufijo = rel.replace(/^\/?api\/v1/, '');
        return `${environment.apiUrl}${sufijo.startsWith('/') ? '' : '/'}${sufijo}`;
    }

    archivo(r: Pick<InboundReceipt, 'idu_inbound' | 'thumb_url'>): Observable<Blob> {
        return this._http.get(this.urlArchivo(r), { responseType: 'blob' });
    }

    opciones(iduInbound: string): Observable<InboundOpcion[]> {
        return this._http.get<unknown>(`${this._base}/${iduInbound}/opciones`).pipe(map(aOpciones));
    }

    confirmar(iduInbound: string, body: ConfirmarInbound): Observable<ResultadoConfirmar> {
        return this._http.post<unknown>(`${this._base}/${iduInbound}/confirmar`, body).pipe(
            map((res) => {
                const d = (desenvolver(res) ?? {}) as Partial<ResultadoConfirmar>;
                return {
                    idu_receipt: d.idu_receipt ?? '',
                    idu_abono: d.idu_abono ?? null,
                    aplicado_a: Array.isArray(d.aplicado_a) ? d.aplicado_a : [],
                };
            }),
        );
    }

    reclamar(iduInbound: string, iduTanda: string): Observable<unknown> {
        return this._http.post<unknown>(`${this._base}/${iduInbound}/reclamar`, { idu_tanda: iduTanda });
    }

    descartar(iduInbound: string, motivo: MotivoDescarte, nota?: string): Observable<unknown> {
        return this._http.post<unknown>(`${this._base}/${iduInbound}/descartar`, {
            motivo,
            ...(nota ? { nota } : {}),
        });
    }
}

// ─── Normalización tolerante de respuestas ─────────────────────────────────

function desenvolver(res: unknown): unknown {
    const r = res as { data?: unknown } | null;
    return r && typeof r === 'object' && 'data' in r ? r.data : res;
}

/** `{ data: [...], total }` o `{ data: { data: [...], total } }`. */
function aPagina(res: unknown): InboundPagina {
    const r = res as { data?: unknown; total?: number } | null;
    if (Array.isArray(r?.data)) return { data: r.data as InboundReceipt[], total: Number(r.total ?? r.data.length) };
    const d = r?.data as { data?: unknown; total?: number } | undefined;
    if (Array.isArray(d?.data)) return { data: d.data as InboundReceipt[], total: Number(d.total ?? d.data.length) };
    if (Array.isArray(res)) return { data: res as InboundReceipt[], total: res.length };
    return { data: [], total: 0 };
}

function aOpciones(res: unknown): InboundOpcion[] {
    let d = desenvolver(res) as unknown;
    if (d && !Array.isArray(d) && typeof d === 'object') {
        const o = d as Record<string, unknown>;
        d = o['opciones'] ?? o['data'] ?? o['rondas'] ?? [];
    }
    return Array.isArray(d) ? d.map(aOpcion).filter((o) => !!o.idu_split && !!o.idu_tanda_number) : [];
}

function aOpcion(raw: unknown): InboundOpcion {
    const o = (raw ?? {}) as Record<string, unknown>;
    const tanda = (o['tanda'] ?? {}) as Record<string, unknown>;
    const s = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));
    const n = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));
    return {
        idu_tanda: s(o['idu_tanda'] ?? tanda['idu_tanda']),
        name_tanda: s(o['name_tanda'] ?? tanda['name'] ?? tanda['name_tanda']),
        idu_split: s(o['idu_split']) ?? '',
        ronda: n(o['ronda'] ?? o['num_order_payment']),
        idu_tanda_number: s(o['idu_tanda_number']) ?? '',
        numero: n(o['numero'] ?? o['num_number']),
        amt_pendiente: n(o['amt_pendiente'] ?? o['amt_due'] ?? o['amt_split']),
        date_due: s(o['date_due'] ?? o['date_payment_due']),
        idu_share: s(o['idu_share']),
        nombre: s(o['nombre'] ?? o['name_participant']),
        cod_currency: s(o['cod_currency']),
    };
}
